import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Icon, Table } from "semantic-ui-react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { HIGHLIGHT_COLUMN_COLOR } from "./adminHelpers";

/* Breathing room left under the scroll box, matching .bhu-main's own bottom
   padding (App.css) so the box's bottom edge lands on the page's bottom
   margin rather than flush against the window. */
const BOTTOM_GAP = 14;
/* Absolute floor: below this the box stops shrinking and the page starts
   scrolling again, however little room the window has. */
const MIN_HEIGHT = 240;
/* Fallback for --header-height (set on <html> by Header.js once the navbar
   has been measured) on the frames before that measurement lands. */
const FALLBACK_HEADER = 60;

/* Applied to a column declaring `meta: { shrink: true }`.

   The table is 100% wide, so whatever width it has over and above its content
   is shared out among the columns — which leaves a column of short, uniform
   values (a booking code, a room number) sitting in a pool of white space it
   has no use for, while the columns holding real text are the ones truncating.
   A width of 1% in an auto-layout table doesn't mean 1%: it asks the browser
   for the narrowest the content allows, and the space that frees goes to the
   columns that didn't ask. */
const SHRINK_TO_CONTENT = { width: "1%" };

/* How tall the scroll box may be, given where it sits in the document.

   A box with room to fit below its own top never moves: the cap is exactly
   what stops the page scrolling, so it is sized from where it naturally
   lands. A box without that room — a short window, or a page that stacks
   several tables, where every table after the first begins below the fold —
   is going to be scrolled to, and the sticky rule pins it under the navbar
   when it is. That pinned position, not its resting one, is the one worth
   sizing for: measuring the space below a natural top that is already past
   the bottom of the window gives a negative number, which is how the last
   table on a stacked page ended up at the MIN_HEIGHT floor no matter how
   many rows it held.

   Pure, so the arithmetic can be tested without a DOM. */
export function heightCap({ naturalTop, windowHeight, headerHeight }) {
  const fitsWhereItSits = naturalTop + MIN_HEIGHT + BOTTOM_GAP <= windowHeight;
  const top = fitsWhereItSits ? naturalTop : Math.min(naturalTop, headerHeight);
  return Math.max(Math.round(windowHeight - top - BOTTOM_GAP), MIN_HEIGHT);
}

/* The window the list opens on: a batch of history above the anchor so there
   is somewhere to scroll up into, and a batch below so the forward sentinel
   isn't already on screen. A full batch of history (~25 compact rows, ~750px)
   also keeps the top sentinel outside its 300px rootMargin on mount, so the
   opening paint doesn't immediately trigger a prepend.

   Deliberately not clamped to the row count — the render clamps both edges,
   and a window that shrank to fit an empty dataset would render no rows on
   the frame the rows arrive, taking the sentinels out of the DOM with them.
   Pure, so the window arithmetic can be tested without a DOM. */
export function seedRange(anchorIndex, batchSize) {
  if (anchorIndex <= 0) return { start: 0, end: batchSize };
  return {
    start: Math.max(0, anchorIndex - batchSize),
    end: anchorIndex + batchSize,
  };
}

/* Generic sortable + infinite-scroll table used across the admin "Manage"
   tabs (Guest Houses, Room Types, Rooms, Admin Users, Bookings, Front Desk,
   Summary). Sorting/rendering are client-side since every consumer already
   holds its full dataset in React state — none of these list endpoints
   support server-side sort/paging. Rows are still rendered incrementally
   (batchSize at a time, growing as the user scrolls) rather than all at
   once, to keep the initial DOM small. */
export default function DataTable({
  data,
  columns,
  batchSize = 25,
  emptyMessage = "No records found.",
  initialSorting = [],
  getRowStyle,
  onRowClick,
  /* Caps the scroll box so the header has something to stick to — see the
     comment on the wrapper below. Left unset by default: the cap is measured
     from where the box actually lands (see autoMaxHeight), which no fixed
     formula can know — every tab puts a different amount of chrome above it.
     Pass a value to pin it explicitly instead. */
  maxHeight,
  /* Left unset by default: narrow tables (Room Types, Payment Options) should
     shrink to fit rather than force a scrollbar. Column-heavy tables pass a
     floor so their cells don't get squeezed to one word per line instead. */
  minWidth,
  /* Optional: a predicate over a row's data picking the row the list should
     open on — for Bookings, the first arrival today or later. Rows before it
     stay reachable, they just load upward as the reader scrolls into them
     instead of being the first thing shown. Only consulted while the table is
     in its default order: once the reader sorts by a column of their own,
     "the first row on or after today" stops meaning anything, so the list
     opens at the top like every other tab. Unset everywhere else, which pins
     the window at row 0 exactly as before. */
  anchorRow,
  /* Opt-in: break out of .bhu-main's centered 1800px cap and span the whole
     window. Off by default because a narrow table (Room Types, Payment
     Options) stretched across a wide monitor reads worse, not better — only
     the column-heavy lists that actually run out of room ask for it. */
  fullBleed = false,
  /* How much of the window a full-bleed table takes, as a fraction. 1 spans
     it edge to edge; anything less stays centered, leaving a margin down both
     sides. Ignored unless fullBleed is set. */
  bleedWidth = 1,
}) {
  const [sorting, setSorting] = useState(initialSorting);
  /* Both edges of the rendered window move, so they live in one object and
     change together. Not named `window` — this file uses the global. */
  const [range, setRange] = useState({ start: 0, end: batchSize });
  const sentinelRef = useRef(null);
  const topSentinelRef = useRef(null);
  /* Latches for the two one-shot scroll corrections below. Refs rather than
     state: they're set where the change is decided and read on a later commit,
     and neither should itself cause a render. */
  const scrollIntent = useRef(null);   // "anchor" | "top" | null
  const prependedFrom = useRef(null);  // scrollHeight recorded before a prepend
  const scrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const anchorRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [screenWidth, setScreenWidth] = useState(0);
  const [autoMaxHeight, setAutoMaxHeight] = useState(null);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    /* Only two click states per column — ascending and descending. Without
       this, react-table's default third click clears sorting entirely,
       which combined with the always-visible arrow looked like a stuck/
       broken toggle. */
    enableSortingRemoval: false,
  });

  const sortedRows = table.getSortedRowModel().rows;

  /* Where the list should open. 0 doubles as "no anchoring asked for" and
     "the first row already matches" — both mean open at the top, so
     `anchorIndex > 0` is the one test everywhere below. */
  const anchorIndex = useMemo(() => {
    if (!anchorRow || sorting.length > 0) return 0;
    const i = sortedRows.findIndex((r) => anchorRow(r.original));
    /* Nothing matched — every row is behind the anchor (a "Checked Out"
       filter, or an Arrival To in the past). The end of the list is then the
       meaningful place to open, not the top. */
    return i === -1 ? sortedRows.length : i;
  }, [anchorRow, sorting, sortedRows]);

  /* Back to the opening window whenever the dataset or sort order changes —
     some consumers filter `data` in place without remounting DataTable, so
     this can't rely on a `key` prop reset. A layout effect rather than a
     passive one: the anchored window has to be in the DOM before the first
     paint, or the box paints once at row 0 and visibly jumps to today. */
  useLayoutEffect(() => {
    const next = seedRange(anchorIndex, batchSize);
    /* Hand React back the same object when nothing moved rather than a fresh
       one each time: a consumer that rebuilt `data` on every render would
       otherwise loop through this effect forever. */
    setRange((prev) =>
      prev.start === next.start && prev.end === next.end ? prev : next,
    );
    /* Only tables that asked for an anchor get their scroll position touched;
       everywhere else this stays null and the box behaves as it always has. */
    scrollIntent.current = anchorIndex > 0 ? "anchor" : anchorRow ? "top" : null;
  }, [data, sorting, batchSize, anchorIndex, anchorRow, sortedRows]);

  const total = sortedRows.length;
  /* The window is re-seeded from an effect, so one render can happen with a
     window left over from a longer dataset. slice() copes with an
     overshooting end; a start past the end would render nothing, so fall back
     to the top for that one frame. */
  const winEnd = Math.min(range.end, total);
  const winStart = range.start < winEnd ? range.start : 0;
  const rows = sortedRows.slice(winStart, winEnd);
  const hasMore = winEnd < total;
  const hasEarlier = winStart > 0;

  useEffect(() => {
    if (!hasMore) return;
    /* Both observers stay off until the box has a height cap. Without one it
       doesn't scroll at all, every sentinel is trivially in view, and the
       window would run away to the whole dataset in a couple of frames. */
    if (!(maxHeight || autoMaxHeight)) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          /* Appending never needs scroll compensation — content below the
             viewport doesn't move what's in it. */
          setRange((r) => ({ ...r, end: Math.min(r.end + batchSize, sortedRows.length) }));
        }
      },
      /* Preload the next batch a bit before the sentinel actually reaches
         the bottom of the scroll box, so growth feels continuous rather than
         a pop-in. The root is that box, not the viewport — rows are clipped
         inside it, so a viewport-rooted margin would never see them coming. */
      { root: scrollRef.current, rootMargin: "300px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
    /* rows.length, not just hasMore: the sentinel lives inside the rendered
       row set, so a commit that changes which rows exist can replace or
       remove its node. Without this the observer can be built on a frame
       where the ref is still null and then never rebuilt, because every other
       dependency has already reached its final value — the table then just
       stops loading. */
  }, [hasMore, rows.length, sortedRows.length, batchSize, maxHeight, autoMaxHeight]);

  /* The mirror of the observer above: earlier rows load as the reader scrolls
     up into the history that sits above the anchor. */
  useEffect(() => {
    if (!hasEarlier) return;
    if (!(maxHeight || autoMaxHeight)) return;
    const el = topSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        /* Where the box stood before React prepends anything; the layout
           effect below reads it once the new rows are in and puts the reader
           back on the row they were looking at. First writer wins — the
           observer can fire again before the re-render lands, and the DOM
           hasn't changed in between, so the value would be the same. */
        if (prependedFrom.current == null) {
          prependedFrom.current = scrollRef.current?.scrollHeight ?? null;
        }
        /* Deliberately a new object even at start === 0, so a commit always
           follows and the latch above always clears — one that could survive
           a no-op update would corrupt the *next* prepend. */
        setRange((r) => ({ ...r, start: Math.max(0, r.start - batchSize) }));
      },
      /* Top edge only — the forward sentinel's margin applies to both edges
         and would be meaningless below this row. */
      { root: scrollRef.current, rootMargin: "300px 0px 0px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasEarlier, rows.length, batchSize, maxHeight, autoMaxHeight]);

  /* Track how wide the table actually is vs. the space it has, so the proxy
     scrollbar can be sized to match and hidden entirely when it isn't needed. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      setContentWidth(el.scrollWidth);
      setViewportWidth(el.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    const tableEl = el.querySelector("table");
    if (tableEl) observer.observe(tableEl);
    return () => observer.disconnect();
  }, [columns, data, range]);

  /* The window's usable width for a full-bleed table. It has to be measured
     rather than written as 100vw: index.css keeps a vertical scrollbar on
     `html` at all times, and 100vw includes that scrollbar, so a 100vw table
     would overhang the visible area (and be clipped by body's overflow-x).
     documentElement.clientWidth excludes it. Being in CSS pixels, it also
     tracks browser zoom — zooming fires resize and re-measures. Layout effect,
     so the width is in place before the first paint instead of one frame of
     the table at its capped width. */
  useLayoutEffect(() => {
    if (!fullBleed) return undefined;
    const measure = () => setScreenWidth(document.documentElement.clientWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [fullBleed]);

  /* Cap the box at the space the window actually leaves it (see heightCap),
     so it ends at the bottom of the window instead of running past it and
     making the whole page scroll on top of the table's own scrolling. Where
     the box sits depends on how much chrome each tab stacks above it (tab
     bar, stat row, filters, list header) and, on a page that stacks several
     tables, on the tables above it, so it has to be measured rather than
     written as a formula — the old `100vh - header - 28px` assumed the box
     began right under the navbar, which it never does.

     The reading is taken from a zero-height anchor rendered just before the
     box, not the box itself: the box is sticky, so once the page scrolls its
     own top clamps to the navbar height and no longer reports where it
     naturally sits. Adding scrollY makes the result scroll-independent —
     normally moot, since a correct cap is exactly what stops the page
     scrolling, but it keeps the first measurement right if the page happens
     to be scrolled when this runs.

     Layout effect + ResizeObserver on body, so the box is sized before the
     first paint and re-sized whenever the space above it changes: a window
     resize, the filter row wrapping at a narrow width, a notice banner
     appearing, the navbar growing to three lines for a GH_ADMIN. A box's own
     height can't feed back into its own cap — the anchor sits above it — so
     stacked tables settle: each one's cap depends only on what is above it. */
  useLayoutEffect(() => {
    if (maxHeight) return undefined;
    const measure = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const naturalTop = anchor.getBoundingClientRect().top + window.scrollY;
      const headerHeight =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--header-height",
          ),
        ) || FALLBACK_HEADER;
      const next = heightCap({
        naturalTop,
        windowHeight: window.innerHeight,
        headerHeight,
      });
      setAutoMaxHeight((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [maxHeight]);

  /* Two one-shot scroll corrections, driven by the latches above rather than
     by a dependency list: what they wait on (the height cap being measured,
     the anchor row reaching the DOM) lands across several commits, so this
     runs after every commit and does nothing until what it needs is there.
     Layout effects, so the correction is applied before the browser paints
     the new rows — a passive effect shows one frame at the wrong offset. */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (scrollIntent.current) {
      /* Until the cap is measured the box is as tall as its contents:
         scrollHeight === clientHeight, and any scrollTop written here is
         silently dropped. Leave the latch armed and try again next commit. */
      if (!(maxHeight || autoMaxHeight)) return;
      const intent = scrollIntent.current;
      scrollIntent.current = null;
      /* An absolute position supersedes any prepend correction in flight, and
         clearing it here stops the two from stacking. */
      prependedFrom.current = null;

      if (intent === "top") {
        el.scrollTop = 0;
        return;
      }
      const rowEl = el.querySelector("[data-anchor-row]");
      if (!rowEl) {
        /* Nothing at or after the anchor, so the whole list sits behind it
           and the last row is where the reader wants to start. */
        el.scrollTop = el.scrollHeight;
        return;
      }
      /* The column header is sticky at the top of the box, so a row parked at
         its own offset lands underneath it — back off by the header's height. */
      const headH = el.querySelector("thead")?.getBoundingClientRect().height ?? 0;
      const top =
        rowEl.getBoundingClientRect().top -
        el.getBoundingClientRect().top +
        el.scrollTop -
        headH;
      el.scrollTop = Math.max(0, top);
      return;
    }

    const before = prependedFrom.current;
    if (before == null) return;
    prependedFrom.current = null;
    /* Rows aren't a uniform height — singleLine stops wrapping but a status
       badge or a taller glyph still varies the row box, and the top sentinel
       row itself disappears on the commit that reaches the start — so the
       shift a prepended batch causes is measured, not multiplied out from a
       row count. */
    const grew = el.scrollHeight - before;
    if (grew) el.scrollTop += grew;
  });

  /* Standard full-bleed: 50% is half the (capped, padded) parent, so pulling
     back half the table's own width lands it centred on the window — at
     bleedWidth 1 that means flush with both edges, and below 1 an even margin
     either side. */
  const bleedPx = Math.round(screenWidth * bleedWidth);
  const fullBleedStyle =
    fullBleed && screenWidth
      ? { width: bleedPx, marginLeft: `calc(50% - ${bleedPx / 2}px)` }
      : null;

  const needsHScroll = contentWidth > viewportWidth + 1;

  /* Keep the proxy bar and the real scroll box in step. Assigning scrollLeft
     fires the other element's scroll handler, but the equality guard makes
     that second pass a no-op instead of a feedback loop. */
  const syncFrom = (fromRef, toRef) => () => {
    const from = fromRef.current;
    const to = toRef.current;
    if (!from || !to) return;
    if (to.scrollLeft !== from.scrollLeft) to.scrollLeft = from.scrollLeft;
  };

  return (
    <>
      {/* `body { overflow-x: hidden }` (index.css) clips anything wider than
         the viewport with no way to scroll to it, so the table needs its own
         scroll box. That box is also what makes the sticky header work:
         overflow-x:auto forces overflow-y to compute as auto, making this div
         the nearest scroll container, so a header sticking to `top: 0` needs
         the div itself to be height-capped — otherwise it just scrolls away
         with the page.

         The whole assembly is then sticky at the navbar's height, so once the
         page scrolls past it the box pins directly under the fixed navbar and
         the column header — stuck to the top of the box — lands right beneath
         it. z-index stays below the navbar's 100 so it slides underneath.

         A native horizontal scrollbar can only render at the bottom edge of a
         scroll box, so the bar at the top is a proxy: an empty div of the same
         scroll width whose scrollLeft is mirrored onto the table. */}
      <style>{`
        .bhu-datatable-sticky {
          position: sticky;
          top: var(--header-height, 60px);
          z-index: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          background: var(--surface, #fff);
        }
        .bhu-datatable-topscroll {
          overflow-x: auto;
          overflow-y: hidden;
          flex: 0 0 auto;
        }
        .bhu-datatable-topscroll::-webkit-scrollbar { height: 10px; }
        .bhu-datatable-topscroll::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.06);
          border-radius: 5px;
        }
        .bhu-datatable-topscroll::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.28);
          border-radius: 5px;
        }
        .bhu-datatable-scroll {
          overflow: auto;
          flex: 1 1 auto;
          min-height: 0;
          /* Rows are prepended above the viewport when the reader scrolls up,
             and the scroll position is corrected by hand immediately after.
             Chrome and Firefox try to do the same thing at the same moment
             and the two corrections stack into a jump; Safari doesn't
             implement it at all, so it can't be leaned on instead. */
          overflow-anchor: none;
          /* index.css sets smooth scrolling on html. It isn't inherited, but
             say so here anyway: the corrections above are instant
             repositions, and animating one would leave the box mid-flight
             when the next batch lands. */
          scroll-behavior: auto;
        }
        .bhu-datatable-scroll .ui.table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          /* Rows scroll underneath this cell, so its background has to be
             fully opaque or they show through it. Semantic's own hover and
             sorted states drop in a translucent rgba(0,0,0,.05) that does
             exactly that — ghost rows sliding through the header text — so
             both are restated here as the same shade pre-blended over the
             header's #F9FAFB, which looks identical but hides what's behind. */
          background: #f9fafb;
        }
        .bhu-datatable-scroll .ui.sortable.table thead th:hover,
        .bhu-datatable-scroll .ui.sortable.table thead th.sorted,
        .bhu-datatable-scroll .ui.sortable.table thead th.sorted:hover {
          background: #edeeee;
        }
        .bhu-datatable-scroll .ui.table { margin-top: 0; }
        /* Tighter than Semantic's own compact table (.5em .7em), which still
           leaves a band of dead space above and below every value. These rows
           hold one line each (the table is singleLine), so the padding is
           pure height: trimming it fits several more bookings on a screen
           without crowding the text, which is the whole point of a list you
           scan. Applied to th and td together so the header keeps sitting on
           the same rhythm as the rows under it. */
        .bhu-datatable-scroll .ui.table th,
        .bhu-datatable-scroll .ui.table td {
          padding: 0.35em 0.7em;
          /* Up from Semantic's 1em — the list is dense and gets scanned a row
             at a time, so the text is worth the extra couple of pixels. */
          font-size: 1.05rem;
        }
        /* Semantic's 1.4 leaves a slack band inside the line box itself. */
        .bhu-datatable-scroll .ui.table td { line-height: 1.25; }
        /* The labels living in cells — the status badge, the room chips —
             carry real padding so the pill reads as a pill and the room
             number has room to breathe. Semantic's own .5833em still makes a
             label noticeably taller than the line beside it, so this sits
             between the two: enough to shape the badge, not so much that it
             props the row up from the inside. Scoped to table cells, so
             labels elsewhere (modals, filter bar, the counts beside a
             heading) keep their normal size. */
        .bhu-datatable-scroll .ui.table td .ui.label {
          padding: 0.4em 0.7em;
        }
        /* Room chips carry the one value their column exists for, so they
           read at nearly body size rather than Semantic's ~11px "small". */
        .bhu-datatable-scroll .ui.table td .ui.label.bhu-room-chip {
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          padding: 0.35em 0.6em;
        }
        /* Semantic paints yellow/orange labels white-on-colour with
           !important, which is barely legible on #FBBD08; dark text keeps
           the colour and makes the room number readable. */
        .bhu-datatable-scroll .ui.table td .ui.yellow.label.bhu-room-chip,
        .bhu-datatable-scroll .ui.table td .ui.orange.label.bhu-room-chip {
          color: rgba(0, 0, 0, 0.87) !important;
        }
        /* A circular label is sized by min-width/min-height rather than its
           padding, and Semantic marks that padding !important — which an
           inline style can't reach, so the override belongs here too. */
        .bhu-datatable-scroll .ui.table td .ui.circular.label {
          min-width: 1.9em;
          min-height: 1.9em;
          padding: 0.45em !important;
        }
      `}</style>
      {/* Measurement anchor for the height cap above — an ordinary sibling,
          so unlike the sticky box it always reports where the box naturally
          sits. Zero height, so it costs the layout nothing. */}
      <div ref={anchorRef} aria-hidden style={{ height: 0 }} />
      <div
        className="bhu-datatable-sticky"
        style={{ maxHeight: maxHeight ?? autoMaxHeight ?? undefined, ...fullBleedStyle }}
      >
      {needsHScroll && (
        <div
          ref={topScrollRef}
          className="bhu-datatable-topscroll"
          onScroll={syncFrom(topScrollRef, scrollRef)}
        >
          <div style={{ width: contentWidth, height: 1 }} />
        </div>
      )}
      <div
        ref={scrollRef}
        className="bhu-datatable-scroll"
        onScroll={syncFrom(scrollRef, topScrollRef)}
      >
      {/* singleLine: one line per row, so the reader gets more rows per screen
          instead of cells wrapping onto two or three. Semantic's rule is just
          white-space: nowrap — nothing is truncated; a long value widens the
          table and the scroll box above handles it. */}
      <Table sortable celled compact singleLine selectable={rows.length > 0} style={{ minWidth }}>
        <Table.Header>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sortable = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();
                const highlight = header.column.columnDef.meta?.highlight;
                return (
                  <Table.HeaderCell
                    key={header.id}
                    onClick={
                      sortable
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    style={{
                      ...(sortable ? { cursor: "pointer" } : undefined),
                      ...(highlight ? { backgroundColor: HIGHLIGHT_COLUMN_COLOR } : undefined),
                      ...(header.column.columnDef.meta?.shrink ? SHRINK_TO_CONTENT : undefined),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {/* One icon, one rendering path, for every column and
                             every state — default (unsorted) and descending
                             both read as a down arrow, ascending as an up
                             arrow, so clicking is a clean two-way toggle
                             instead of cycling through a third look supplied
                             by Semantic's own built-in sorted-column icon. */}
                          {sortable && (
                            <Icon
                              name={sortDir === "asc" ? "sort up" : "sort down"}
                              style={{ marginLeft: 6 }}
                            />
                          )}
                        </>
                      )}
                  </Table.HeaderCell>
                );
              })}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body>
          {rows.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={columns.length} textAlign="center">
                {emptyMessage}
              </Table.Cell>
            </Table.Row>
          ) : (
            <>
              {hasEarlier && (
                <Table.Row>
                  <Table.Cell colSpan={columns.length} textAlign="center" style={{ padding: 10, color: "rgba(0,0,0,0.4)" }}>
                    <div ref={topSentinelRef}>
                      <Icon name="spinner" loading /> Loading earlier…
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
              {rows.map((row, i) => (
                <Table.Row
                  key={row.id}
                  /* Semantic's Table.Row is a plain function component, so it
                     can't take a ref — but it spreads unknown props onto the
                     <tr>, so the one-shot scroll positioning finds this row by
                     attribute instead. row.id is the row's index in the *data*
                     order, not the sorted order, so the sorted position has to
                     be reconstructed from the slice. */
                  data-anchor-row={
                    anchorIndex > 0 && winStart + i === anchorIndex ? "" : undefined
                  }
                  style={{
                    ...(getRowStyle ? getRowStyle(row.original) : undefined),
                    ...(onRowClick ? { cursor: "pointer" } : undefined),
                  }}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      style={{
                        ...(cell.column.columnDef.meta?.highlight
                          ? { backgroundColor: HIGHLIGHT_COLUMN_COLOR }
                          : undefined),
                        ...(cell.column.columnDef.meta?.shrink ? SHRINK_TO_CONTENT : undefined),
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
              {hasMore && (
                <Table.Row>
                  <Table.Cell colSpan={columns.length} textAlign="center" style={{ padding: 10, color: "rgba(0,0,0,0.4)" }}>
                    {/* Table.Cell isn't a forwardRef component, so the
                       observer target has to be a plain DOM node inside it. */}
                    <div ref={sentinelRef}>
                      <Icon name="spinner" loading /> Loading more…
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </>
          )}
        </Table.Body>
      </Table>
      </div>
      </div>
    </>
  );
}
